import {
  DataProvider,
  BaseRecord,
  CrudFilter,
  CrudSort,
  CustomParams,
  CustomResponse,
} from "@refinedev/core";
import { GraphQLClient, Variables } from "graphql-request";
import { config } from "../config/env";

export const API_URL = config.graphqlEndpoint || config.hasuraUrl;
export const HEALTH_CHECK_URL = `${API_URL}/health`;

const client = new GraphQLClient(API_URL);

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const { current = 1, pageSize = 10 } = pagination ?? {};

    const response = await client.request(meta?.gqlQuery ?? "", {
      ...meta?.variables,
      skip: (current - 1) * pageSize,
      take: pageSize,
    } as Variables);

    return {
      data: (response as any)[resource] ?? [],
      total: (response as any).total ?? 0,
    };
  },

  getMany: async ({ resource, ids, meta }) => {
    const response = await client.request(meta?.gqlQuery ?? "", {
      ...meta?.variables,
      where: { id: { in: ids } },
    } as Variables);

    return {
      data: (response as any)[resource] ?? [],
    };
  },

  create: async ({ resource, variables, meta }) => {
    const response = await client.request(meta?.gqlMutation ?? "", {
      input: variables,
    } as Variables);

    return {
      data: (response as any)[resource] ?? {},
    };
  },

  update: async ({ resource, id, variables, meta }) => {
    const response = await client.request(meta?.gqlMutation ?? "", {
      id,
      input: variables,
    } as Variables);

    return {
      data: (response as any)[resource] ?? {},
    };
  },

  updateMany: async ({ resource, ids, variables, meta }) => {
    const response = await client.request(meta?.gqlMutation ?? "", {
      where: { id: { in: ids } },
      input: variables,
    } as Variables);

    return {
      data: (response as any)[resource] ?? [],
    };
  },

  getOne: async ({ resource, id, meta }) => {
    const response = await client.request(meta?.gqlQuery ?? "", {
      id,
    } as Variables);

    return {
      data: (response as any)[resource] ?? {},
    };
  },

  deleteOne: async ({ resource, id, meta }) => {
    const response = await client.request(meta?.gqlMutation ?? "", {
      id,
    } as Variables);

    return {
      data: (response as any)[resource] ?? {},
    };
  },

  deleteMany: async ({ resource, ids, meta }) => {
    const response = await client.request(meta?.gqlMutation ?? "", {
      where: { id: { in: ids } },
    } as Variables);

    return {
      data: (response as any)[resource] ?? [],
    };
  },

  getApiUrl: () => {
    return API_URL;
  },

  custom: async <
    TData extends BaseRecord = BaseRecord,
    TQuery = unknown,
    TPayload = unknown,
  >({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
    meta,
  }: CustomParams<TQuery, TPayload>): Promise<CustomResponse<TData>> => {
    let requestUrl = `${url}`;

    if (query) {
      requestUrl = `${requestUrl}?${new URLSearchParams(query as Record<string, string>).toString()}`;
    }

    if (headers) {
      client.setHeaders(headers);
    }

    const variables: Variables =
      method === "get"
        ? ({ filters, sorters } as Variables)
        : (payload as Variables);

    const response = await client.request(
      meta?.gqlQuery ?? meta?.gqlMutation ?? "",
      variables,
    );

    return {
      data: response as TData,
    };
  },
};

export const liveProvider = {
  subscribe: () => {
    return {
      unsubscribe: () => undefined,
    };
  },
  unsubscribe: () => {
    return;
  },
};
